<template>
  <div class="h-100">
    <v-card elevation="0" outlined class="h-100" v-if="team && !loading" width="100%" min-height="350">
      <v-card-title class="py-1 white--text d-flex align-center justify-space-between" style="flex-wrap: nowrap;" :class="$vuetify.theme.dark ? 'black' : 'primary'" >
        <div class="d-flex align-center" style="flex: 1 1 auto; min-width: 0;">
          <v-icon color="white" medium class="mr-2">shield</v-icon>
          <span class="white--text body-font body-1 font-weight-bold text-uppercase text-ellipsis">{{$t("dashboards.myTeam")}}</span>
        </div>
        <v-btn text small class="white--text font-weight-bold pl-0 pr-0" :to="`/team/${teamId}`" style="flex-shrink: 0;">
          <span class="text-decoration-underline">{{$t('dashboards.seeMyTeam')}}</span>
          <v-icon small class="ml-1" color="white">keyboard_arrow_right</v-icon>
        </v-btn>
      </v-card-title>
      
      <!-- Top row: crest + text block on the left, team value on the right -->
      <v-row no-gutters class="pb-4 px-4 pt-2">
        <!-- Left block: crest + text use flex so gap is consistent across breakpoints -->
        <v-col cols="12" xl="8">
          <div class="d-flex">
            <!-- Crest -->
            <router-link :to="`/team/${team.teamId}`">
              <div
                v-bind:class="{'grey lighten-3 rounded-lg': !$vuetify.theme.dark,'black lighten-3 rounded-lg': $vuetify.theme.dark}"
                class="pa-1 d-flex align-center justify-center mr-4"
                style="width: 90px; height: 90px;"
              >
                <img
                  :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + team.teamId + '.png'"
                  :height="$vuetify.breakpoint.smAndDown ? '60' : '80'"
                  contain
                  @error="onImageLoadFailure($event,'team')"
                />
              </div>
            </router-link>

            <!-- Text stack -->
            <div class="d-flex flex-column justify-center">
              <!-- Team Name -->
              <router-link
                class="text-h6 font-weight-bold primary--text"
                :to="`/team/${team.teamId}`"
                style="text-decoration: none; line-height: 1.2;"
              >{{team.teamName}}</router-link>

              <!-- League Badge with Position -->
              <div 
                v-if="team.leagueId && team.ranking"
                class="d-flex align-center px-2 py-1 rounded mt-1"
                :class="$vuetify.theme.dark ? 'black lighten-2' : 'grey lighten-4'"
                style="width: fit-content;"
              >
                <img
                  :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + team.leagueId + '.png'"
                  height="14"
                  contain
                  class="mr-1"
                  @error="onImageLoadFailure($event,'league')"
                />
                <span class="caption font-weight-bold primary--text mr-1">#{{ team.ranking }}</span>
                <span class="caption primary--text mr-1">at</span>
                <span class="caption primary--text mr-1">{{ team.leagueName }}</span>
                <DriblabCountryFlag 
                  v-if="team.countryCode"
                  :iso="team.countryCode"
                  class="ml-1"
                  style="width: 14px; height: 10px;"
                ></DriblabCountryFlag>
              </div>

              <!-- ELO Badge (always below league badge) -->
              <div class="mt-2" v-if="team.elo">
                <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <div 
                      v-bind="attrs" 
                      v-on="on"
                      class="d-flex align-center rounded py-1 px-2"
                      :class="$vuetify.theme.dark ? 'black lighten-2' : 'grey lighten-4'"
                      style="width: fit-content; cursor: pointer;"
                    >
                      <v-chip x-small class="pa-0 px-2 body-2 font-weight-bold white--text mr-1 monospace-font" color="grey darken-2">ELO</v-chip>
                      <span class="body-2 text-ellipsis">
                        <b class="mr-1">{{ Math.floor(team.elo) }}</b>
                        <span v-if="team.diffElo !== 0 && team.diffElo !== null" v-bind:class="{'success--text': team.diffElo > 0,'error--text':team.diffElo < 0}">
                          ({{team.diffElo > 0 ? '+' : ''}}{{Math.floor(team.diffElo)}})
                        </span>
                      </span>
                    </div>
                  </template>
                  <div class="d-flex flex-column black--text body-1">
                    <span class="font-weight-bold mb-1">ELO Rating</span>
                    <span>Current: <b>{{ Math.floor(team.elo) }}</b></span>
                    <span v-if="team.diffElo !== 0 && team.diffElo !== null">
                      Change: 
                      <b :class="team.diffElo > 0 ? 'success--text' : 'error--text'">
                        {{ team.diffElo > 0 ? '+' : '' }}{{ Math.floor(team.diffElo) }}
                      </b>
                    </span>
                    <span v-if="team.rankElo">Rank: <b>#{{ team.rankElo }}</b></span>
                    <span v-if="team.diffRankElo !== 0 && team.diffRankElo !== null">
                      Rank Change: 
                      <b :class="team.diffRankElo > 0 ? 'success--text' : 'error--text'">
                        {{ team.diffRankElo > 0 ? '+' : '' }}{{ team.diffRankElo }}
                      </b>
                    </span>
                    <span v-if="team.dateEloUpdated" class="mt-1 body-2">Updated: {{ team.dateEloUpdated }}</span>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Team Value Badge Column -->
        <v-col cols="12" xl="4" class="align-self-stretch mt-2">
          <div class="d-flex justify-center align-center pa-2 h-100 rounded-lg"
            v-bind:class="{'grey lighten-3 rounded-lg border': !$vuetify.theme.dark,'black lighten-3 rounded-lg border': $vuetify.theme.dark}">
            <v-tooltip top color="lightSecondary lighten-4" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div 
                  v-bind="attrs" 
                  v-on="on"
                  class="d-flex align-center"
                  style="cursor: pointer;"
                >
                  <span class="primary--text font-weight-bold text-sm-h5 body-2">{{currentUserCurrencySymbol}}</span>
                  <h2
                    class="px-1 primary--text font-weight-bold monospace-font"
                  >{{ team.marketValue | formatCash(currentUserRate) }}</h2>
                </div>
              </template>
              <span class="black--text body-1">Team Market Value</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <!-- Divider -->
      <v-divider class="mb-3"></v-divider>

      <!-- Stats: Multi Column Layout -->
      <v-card-text class="px-4 pt-0 pb-4">
        <v-row no-gutters>
          <!-- Coach/Manager (First) -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2" v-if="team.coachName">
            <div class="d-flex flex-column">
              <span class="body-2 primary--text subheader-font">{{$t("team.coach")}}</span>
              <span class="body-1 font-weight-medium primary--text">{{ team.coachName }}</span>
            </div>
          </v-col>
          <!-- Stadium (Second) -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2">
            <div class="d-flex flex-column">
              <span class="body-2 primary--text subheader-font">{{$t("team.stadium")}}</span>
              <span class="body-1 font-weight-medium primary--text">
                {{ team.stadiumName ? team.stadiumName : '-' }}
              </span>
            </div>
          </v-col>
          <!-- Squad Size -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2">
            <div class="d-flex flex-column">
              <span class="body-2 primary--text subheader-font">{{$t("team.roster")}}</span>
              <span class="body-1 font-weight-medium primary--text">
                {{ team.squadSize ? team.squadSize : '-' }} {{ $t('searchbar.players') }}
              </span>
            </div>
          </v-col>
          <!-- Avg. age -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2" v-if="team.averageAge">
            <div class="d-flex flex-column">
            <span class="body-2 primary--text subheader-font">{{$t("team.averageAge")}}</span>
              <span class="body-1 font-weight-medium primary--text">{{ team.averageAge.toFixed(1) }}</span>
          </div>
          </v-col>
          <!-- Freq. Formation -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2">
            <div class="d-flex flex-column">
              <span class="body-2 primary--text subheader-font">{{$t("team.preferredFormation")}}</span>
              <span class="body-1 font-weight-medium primary--text">
                <template v-if="team.preferredFormation">
                  {{ team.preferredFormation }}
                  <span v-if="team.preferredFormationUses" class="ml-1 body-2">
                    ({{ team.preferredFormationUses }} {{$t("general.times")}})
                  </span>
                </template>
                <template v-else>-</template>
              </span>
          </div>
        </v-col>
          <!-- Transfer Balance -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2" v-if="team.transferBalance !== null && team.transferBalance !== undefined">
            <div class="d-flex flex-column">
            <span class="body-2 primary--text subheader-font">{{$t("team.transferBalance")}}</span>
              <span class="body-1 font-weight-medium primary--text">{{ team.transferBalance | formatCash(currentUserRate) }}</span>
          </div>
           </v-col>
          <!-- Youth Team -->
           <v-col cols="6" sm="6" md="4" lg="3" class="pr-3 mb-2">
            <div class="d-flex flex-column">
              <span class="body-2 primary--text subheader-font">{{$t("team.childTeam")}}</span>
              <span class="body-1 font-weight-medium primary--text">
                {{ team.childName ? team.childName : '-' }}
              </span>
          </div>
           </v-col>
      </v-row>
      </v-card-text>
    </v-card>
    <v-card class="d-flex justify-center align-center py-8 px-12" v-if="loading" elevation="0" outlined>
      <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
    </v-card>
  </div>
</template>

<script>
import TeamService from "../../../services/team/team.service";
import { formatCash,getCurrencies } from "../../../utils/functions";
import DriblabCountryFlag from '../../base/DriblabCountryFlag';

export default {
  name: "TeamWidget",
  props: {
    teamId: {
      type: Number,
      required: true
    }
  },
  components:{
    DriblabCountryFlag
  },
  data() {
    return {
      team: null,
      loading: false
    };
  },
  filters: {
    formatCash(n,rate) {
      return formatCash(n * rate)
    }
  },
  computed: {
    currentUserCurrency(){
      return this.$store.getters["auth/currency"];
    },
    currentUserRate(){
      let rate = this.currentUserCurrency === "EUR" ? 1 : this.$store.getters["exchange/rates"][this.$store.getters["auth/currency"]];
      return rate ? rate : 1;
    },
    currencies(){
      return getCurrencies();
    },
    currentUserCurrencySymbol(){
      let currentCurrencyObj = _.find(this.currencies,(c) => c.code === this.currentUserCurrency)
      return currentCurrencyObj.symbol;
    }
  },
  mounted() {
    this.loadViewData();
  },
  methods: {
    loadViewData() {
      this.loadTeam();
    },
    async loadTeam() {
      this.loading = true;
      let teamResponse = await TeamService.getTeamInfo(this.teamId);
      this.team = teamResponse.data;
      
      // Try to get roster to check squad size
      try {
        let rosterResponse = await TeamService.getCurrentRoster(this.teamId);
        // Get squadSize from roster
        if (rosterResponse.data && Array.isArray(rosterResponse.data)) {
          this.team.squadSize = rosterResponse.data.length;
        }
      } catch (error) {
        // Silently fail if roster can't be loaded
      }
      
      this.loading = false;
    },
    onImageLoadFailure(event, type) {
      if (type === "team") {
        event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
      } else if (type === "league") {
        event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/0.png";
      }
    }
  }
};
</script>
